import type { EstimateExtractionItem } from "types/estimateExtraction";

/**
 * Pièces à 0,00 € : non valorisées dans ServiceBox/Alpha, pas offertes.
 *
 * Miroir de `find_unpriced_parts` (heric-back app/services/devis_unpriced_parts.py),
 * qui a le dernier mot : create-token refuse l'envoi sans `allow_unpriced_parts`.
 * OR-1187373 (28/09) : pièce 0000051787565 envoyée à 0,00 €, signée à 286,86 € TTC,
 * valorisée 386,02 € HT après le transfert Alpha.
 *
 * Une ligne PIÈCE dont le prix unitaire ET le total sont nuls (ou absents), sauf :
 * forfait dont le prix de paquet couvre les lignes (total > somme des lignes), ou
 * pièce / ligne de travail qui se dit offerte (geste, garantie, offert, gratuit).
 */
export type UnpricedPart = { item_id: string; item_libelle: string; libelle: string };

const FREE_RE =
  /\b(geste|offert|offerte|offerts|offertes|gratuit|gratuite|gratuits|gratuites|garantie|gracieux|gracieuse)\b/;

function fold(text: string | null | undefined): string {
  return (text || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

const isFree = (label: string | null | undefined) => FREE_RE.test(fold(label));
const isZero = (v: number | null | undefined) => v == null || Math.abs(v) < 0.005;

export function findUnpricedParts(
  items: EstimateExtractionItem[] | null | undefined,
  selectedIds?: (id: string) => boolean,
): UnpricedPart[] {
  const found: UnpricedPart[] = [];
  for (const item of items || []) {
    if (selectedIds && !selectedIds(item.id)) continue;
    if (isFree(item.libelle)) continue;
    const subs = item.sub_items || [];
    const linesTotal = subs.reduce((s, sub) => s + (sub.total || 0), 0);
    // Forfait : le prix du paquet couvre des composants laissés à 0.
    if (item.total != null && item.total > linesTotal + 0.02) continue;
    for (const sub of subs) {
      if (sub.type !== "piece" || isFree(sub.libelle)) continue;
      if (isZero(sub.total) && isZero(sub.unit_price)) {
        found.push({
          item_id: item.id,
          item_libelle: (item.libelle || "").trim(),
          libelle: (sub.libelle || "").trim(),
        });
      }
    }
  }
  return found;
}

export function describeUnpricedParts(parts: UnpricedPart[]): string {
  return parts
    .map((p) => (p.item_libelle ? `${p.libelle} (${p.item_libelle})` : p.libelle))
    .join(", ");
}
